import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { unwrapApiArray } from "../../shared/utils/apiResponse";
import { handleGetPublicGames, handleGetThirdPartyUserCheckinRequest, handleUserMatchCheckin } from "../../userDashboard/userControllers/gameController";
import Banner from "../components/Banner";
import Footer from "../components/Footer";
import RegisterConfirmed from "../components/matchVerifyComps/RegisterConfirmed";
import RegisterError from "../components/matchVerifyComps/RegisterError";
import Navbar from "../components/Navbar";
import landingAsset from "../utils/assetPaths";

import load from "./assets/load.gif";

interface Booking {
    id: string;
    title: string;
    date: string;
    pitch?: {
        name: string;
    };
}

interface Game {
    id: string | number;
    title: string;
    type?: string;
}

const ThirdPartyCheck: React.FC = () => {
    const { state } = useLocation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [bookings, setBookings] = useState<Booking[]>([]);
    const [step, setStep] = useState(1);
    const [registerConfirmed, setRegisterConfirmed] = useState(false);
    const [registerError, setRegisterError] = useState(false);
    const [games, setGames] = useState<Game[]>([]);

    useEffect(() => {
        const fetchGames = async () => {
            try {
                const data = await handleGetPublicGames<Game[]>();
                setGames(unwrapApiArray<Game>(data));
            } catch (error) {
                console.error(error);
            }
        };
        fetchGames();

        if (state?.email) {
            setEmail(state.email);
            setBookings(state.bookings || []);
            setStep(2);
        }
    }, [state]);

    const handleVerify = async () => {
        if (!email) return;
        setLoading(true);
        try {
            const userData = { email, type: "checkin" };
            handleGetThirdPartyUserCheckinRequest(
                userData,
                (res: unknown) => {
                    const resolved = res as { data: Booking[] };
                    setLoading(false);
                    setBookings(resolved.data);
                    setStep(2);
                },
                () => {
                    setLoading(false);
                    setRegisterError(true);
                }
            );
        } catch (error) {
            setLoading(false);
            setRegisterError(true);
        }
    };

    const handleCheckin = async (gameId: string) => {
        setLoading(true);
        try {
            const userData = { email, type: "checkin", model_type: "Game" };
            handleUserMatchCheckin(
                gameId,
                userData,
                () => {
                    setLoading(false);
                    setRegisterConfirmed(true);
                },
                () => {
                    setLoading(false);
                    setRegisterError(true);
                }
            );
        } catch (error) {
            setLoading(false);
            setRegisterError(true);
        }
    };

    return (
        <>
            <Navbar />
            {registerConfirmed && <RegisterConfirmed setRegisterConfirmed={setRegisterConfirmed} />}
            {registerError && <RegisterError setRegisterError={setRegisterError} />}

            <div
                style={{
                    backgroundImage: `url(${landingAsset("herobg.svg")})`,
                    backgroundSize: "cover",
                }}
                className="h-[500px] md:h-[400px] relative w-full flex justify-center items-center flex-col "
            >
                <div
                    style={{
                        background: "linear-gradient(180deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 17.83%)",
                    }}
                    className=" h-full w-full absolute left-0 top-0 flex flex-col items-center justify-center p-4 text-center"
                >
                    <h1 className="text-white text-4xl md:text-5xl lg:text-6xl font-black font-Manrope uppercase tracking-tight">Third Party Authenticate</h1>
                    <p className=" mt-6 text-center font-Manrope text-lg md:text-xl text-white/90 max-w-2xl leading-relaxed">
                        Secure verification for external platform users. Verify your match registration and check in instantly.
                    </p>
                </div>
            </div>

            <div className="flex flex-col items-center py-24 w-full px-4 md:px-14 bg-gray-50 min-h-[600px]">
                <div className="max-w-4xl w-full mx-auto">
                    {step === 1 ? (
                        <div className="max-w-xl mx-auto bg-white p-12 md:p-16 rounded-[48px] shadow-2xl border border-gray-100">
                            <h2 className="text-3xl font-black text-gray-900 mb-4 text-center">Verify Account</h2>
                            <p className="text-gray-500 font-bold text-center mb-12 italic">Enter your registered email to find your matches.</p>

                            <div className="space-y-8">
                                <div className="space-y-3">
                                    <label className="font-Manrope font-black text-gray-900 text-sm uppercase tracking-widest pl-1">Registration Email</label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="w-full bg-gray-50 border-2 border-transparent focus:border-green-700 focus:bg-white px-6 py-4 rounded-2xl font-Manrope font-bold text-gray-900 transition-all outline-none"
                                        placeholder="e.g. john@externalsite.com"
                                    />
                                </div>
                                <button
                                    onClick={handleVerify}
                                    disabled={loading}
                                    className="w-full bg-green-700 hover:bg-green-800 text-white py-5 rounded-2xl font-black text-xl shadow-xl active:scale-95 flex items-center justify-center disabled:opacity-50"
                                >
                                    {loading ? <img src={load} className="w-6 h-6" alt="Loading" /> : "Find My Matches"}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-12">
                            <div className="flex items-center justify-between">
                                <h2 className="text-3xl font-black text-gray-900 uppercase tracking-tighter">Your Active Bookings</h2>
                                <button onClick={() => setStep(1)} className="text-green-700 font-black hover:underline uppercase tracking-widest text-xs">← Back to Search</button>
                            </div>

                            {bookings.length === 0 ? (
                                <div className="bg-white p-20 rounded-[48px] text-center border-2 border-dashed border-gray-100 italic font-bold text-gray-400 text-xl">
                                    No active bookings found for this email.
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                                    {bookings.map((booking: Booking, i: number) => (
                                        <div key={i} className="bg-white p-10 rounded-[40px] shadow-lg border border-gray-100 group hover:shadow-2xl transition-all duration-500">
                                            <div className="flex justify-between items-center mb-8">
                                                <span className="px-5 py-2 bg-green-100 text-green-700 rounded-full font-black text-[10px] uppercase tracking-widest">Confirmed</span>
                                                <p className="font-bold text-gray-400">{new Date(booking.date).toLocaleDateString()}</p>
                                            </div>
                                            <h3 className="text-3xl font-black text-gray-900 mb-6 group-hover:text-green-700 transition-colors uppercase tracking-tight">{booking.title}</h3>
                                            <p className="text-gray-500 font-bold mb-10 flex items-center gap-2 italic">🏟️ {booking.pitch?.name || "Premium Ground"}</p>
                                            <button
                                                onClick={() => handleCheckin(booking.id)}
                                                disabled={loading}
                                                className="w-full bg-gray-900 hover:bg-green-700 text-white py-5 rounded-2xl font-black text-xl flex items-center justify-center gap-3 transition-all active:scale-95"
                                            >
                                                {loading ? <img src={load} className="w-6 h-6" alt="Loading" /> : "Check In Now"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            <div className="py-24 bg-white px-4 md:px-14 border-t border-gray-50">
                <div className="max-w-7xl mx-auto">
                    <div className="space-y-4 mb-16">
                        <h3 className="text-green-700 font-black uppercase tracking-[0.2em] text-sm">Next Play</h3>
                        <h2 className="text-4xl font-black text-gray-900">Recommended for You</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {games.slice(0, 3).map((game, i) => (
                            <div key={i} className="p-8 bg-gray-50 rounded-[40px] border border-gray-100 group hover:shadow-xl transition-all font-Manrope">
                                <p className="font-black text-green-700 mb-4 uppercase tracking-widest text-[10px]">{game.type || "Match"}</p>
                                <h4 className="text-xl font-black text-gray-900 mb-6 group-hover:text-green-700 transition-colors uppercase tracking-tight leading-none">{game.title}</h4>
                                <button onClick={() => navigate(`/matches/${game.id}`)} className="text-sm font-black uppercase text-gray-400 group-hover:text-gray-900 transition-colors">Details →</button>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <Banner />
            <Footer />
        </>
    );
};

export default ThirdPartyCheck;
